import React, { useState } from 'react';
import classNames from 'classnames';
import { useForm, useFormState } from 'react-final-form';

import { FieldSelect, FieldTextInput } from '../../components';
import {
  applyCapacityChange,
  describeCapacity,
  incrementOptions,
  isBookable,
  minimumOptions,
  normalizeCapacity,
  remainingSeats,
} from '../../util/bookingCapacity';

import css from './BookingCapacityFields.module.css';

/**
 * Total seats, booking increment and minimum booking, as one dependent group.
 *
 * The three depend on each other in one direction — capacity, then increment, then
 * minimum — so changing one can leave the ones below it impossible. Rather than letting a
 * provider save something nobody can book, every change goes through
 * `applyCapacityChange` and the fields below it are rewritten to the nearest legal value.
 *
 * A provider therefore never has to work out which multiples are valid: the dropdowns only
 * ever offer values that will still be there after they pick them.
 *
 * Two ways in, because the editors are not all built the same way:
 *   - `namePrefix` — fields inside a final-form form, by path
 *   - `value` + `onChange` — plain React state, for the slot modals
 *
 * Both render the same body, so the rules cannot drift between them.
 */

const Body = props => {
  // What the provider has typed so far, while they are still typing it. The three numbers
  // are normalised against each other on every change, and a field being edited is empty
  // for as long as it takes to delete the old number — normalising that puts the old
  // number straight back, so the field cannot be edited at all. null means not editing.
  const [draftSeats, setDraftSeats] = useState(null);

  const {
    capacity,
    settle,
    intl,
    disabled,
    totalSeatsLabel,
    maxSeats,
    controlled,
    reservedSeats,
  } = props;

  // Only meaningful when editing a session that already has bookings: a provider can set
  // a rule that nothing remaining can satisfy, and the session quietly stops selling.
  // Saying so here is the difference between capacity and bookable capacity.
  const stranded =
    reservedSeats != null && !isBookable(capacity, reservedSeats)
      ? remainingSeats(capacity, reservedSeats)
      : null;

  const label = (id, defaultMessage) => intl.formatMessage({ id, defaultMessage });
  const seatsLabel =
    totalSeatsLabel || label('BookingCapacityFields.totalSeatsLabel', 'Total seats');
  const incrementLabel = label('BookingCapacityFields.bookingIncrementLabel', 'Booking increment');
  const minimumLabel = label('BookingCapacityFields.minimumBookingLabel', 'Minimum booking');

  const increments = incrementOptions(capacity.totalSeats);
  const minimums = minimumOptions(capacity.totalSeats, capacity.bookingIncrement);

  return (
    <>
      <div className={css.fields}>
        <div className={css.field}>
          {controlled ? (
            <>
              <label className={css.plainLabel} htmlFor="capacityTotalSeats">
                {seatsLabel}
              </label>
              <input
                id="capacityTotalSeats"
                className={css.plainInput}
                type="number"
                min={1}
                max={maxSeats}
                step="1"
                disabled={disabled}
                value={draftSeats ?? capacity.totalSeats}
                onChange={e => {
                  const typed = e.target.value;
                  setDraftSeats(typed);
                  const parsed = Number.parseInt(typed, 10);
                  if (Number.isFinite(parsed) && parsed >= 1) settle({ totalSeats: parsed });
                }}
                // Whatever is left half-typed gives way to the settled number, so an empty
                // field cannot be saved as a capacity.
                onBlur={() => setDraftSeats(null)}
              />
            </>
          ) : (
            <FieldTextInput
              id={props.at('totalSeats')}
              name={props.at('totalSeats')}
              type="number"
              min={1}
              max={maxSeats}
              step="1"
              disabled={disabled}
              label={seatsLabel}
              parse={v => {
                const parsed = Number.parseInt(v, 10);
                return Number.isNaN(parsed) ? '' : parsed;
              }}
              onChange={e => {
                const parsed = Number.parseInt(e.target.value, 10);
                if (Number.isFinite(parsed) && parsed >= 1) settle({ totalSeats: parsed });
              }}
            />
          )}
        </div>

        <div className={css.field}>
          {controlled ? (
            <>
              <label className={css.plainLabel} htmlFor="capacityIncrement">
                {incrementLabel}
              </label>
              <select
                id="capacityIncrement"
                className={css.plainInput}
                disabled={disabled}
                value={capacity.bookingIncrement}
                onChange={e => settle({ bookingIncrement: Number.parseInt(e.target.value, 10) })}
              >
                {increments.map(n => (
                  <option value={n} key={n}>
                    {n}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <FieldSelect
              id={props.at('bookingIncrement')}
              name={props.at('bookingIncrement')}
              disabled={disabled}
              label={incrementLabel}
              onChange={v => settle({ bookingIncrement: Number.parseInt(v, 10) })}
            >
              {increments.map(n => (
                <option value={n} key={n}>
                  {n}
                </option>
              ))}
            </FieldSelect>
          )}
        </div>

        <div className={css.field}>
          {controlled ? (
            <>
              <label className={css.plainLabel} htmlFor="capacityMinimum">
                {minimumLabel}
              </label>
              <select
                id="capacityMinimum"
                className={css.plainInput}
                disabled={disabled}
                value={capacity.minimumBooking}
                onChange={e => settle({ minimumBooking: Number.parseInt(e.target.value, 10) })}
              >
                {minimums.map(n => (
                  <option value={n} key={n}>
                    {n}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <FieldSelect
              id={props.at('minimumBooking')}
              name={props.at('minimumBooking')}
              disabled={disabled}
              label={minimumLabel}
              onChange={v => settle({ minimumBooking: Number.parseInt(v, 10) })}
            >
              {minimums.map(n => (
                <option value={n} key={n}>
                  {n}
                </option>
              ))}
            </FieldSelect>
          )}
        </div>
      </div>

      {/* The only place a provider sees what they have just configured, in words. */}
      <p className={css.explanation}>{describeCapacity(capacity)}</p>

      {stranded !== null ? (
        <p className={css.stranded}>
          {stranded === 0
            ? 'This session is full, so it is no longer taking bookings.'
            : `Only ${stranded} seat${stranded === 1 ? '' : 's'} remain, which is fewer than the ` +
              `minimum of ${capacity.minimumBooking}. Guests cannot book this session until ` +
              'seats free up or you lower the minimum.'}
        </p>
      ) : null}
    </>
  );
};

/** Fields living inside a final-form form, addressed by path. */
const FormFields = props => {
  const { namePrefix } = props;
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });

  const at = field => (namePrefix ? `${namePrefix}.${field}` : field);
  const read = field =>
    namePrefix
      ? namePrefix.split('.').reduce((acc, k) => acc?.[k], values)?.[field]
      : values?.[field];

  const current = {
    totalSeats: read('totalSeats') ?? read('seats'),
    bookingIncrement: read('bookingIncrement'),
    minimumBooking: read('minimumBooking'),
  };

  const settle = change => {
    const next = applyCapacityChange(current, change);
    form.batch(() => {
      form.change(at('totalSeats'), next.totalSeats);
      form.change(at('bookingIncrement'), next.bookingIncrement);
      form.change(at('minimumBooking'), next.minimumBooking);
      // Some callers still read `seats`; keep it in step rather than leaving two numbers
      // that disagree about the same thing.
      if (read('seats') !== undefined) form.change(at('seats'), next.totalSeats);
    });
  };

  return <Body {...props} at={at} capacity={normalizeCapacity(current)} settle={settle} />;
};

/** The same fields driven by ordinary React state. */
const ControlledFields = props => {
  const { value, onChange } = props;
  const settle = change => onChange(applyCapacityChange(value, change));
  return <Body {...props} controlled capacity={normalizeCapacity(value)} settle={settle} />;
};

const BookingCapacityFields = props => {
  const { rootClassName, className, maxSeats = 500, onChange } = props;
  const Fields = typeof onChange === 'function' ? ControlledFields : FormFields;
  return (
    <div className={classNames(rootClassName || css.root, className)}>
      <Fields {...props} maxSeats={maxSeats} />
    </div>
  );
};

export default BookingCapacityFields;
